import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import ErrorBoundary from './components/common/ErrorBoundary';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/common/ProtectedRoute';
import { useVersionCheck } from './hooks/useVersionCheck';

// Auth pages
import Login          from './pages/auth/Login';
import Signup         from './pages/auth/Signup';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword  from './pages/auth/ResetPassword';
import OAuthCallback      from './pages/auth/OAuthCallback';
import MagicLinkVerify    from './pages/auth/MagicLinkVerify';
import SetPassword        from './pages/auth/SetPassword';
import Join                from './pages/auth/Join';
import JoinSuccess         from './pages/auth/JoinSuccess';
import Watch               from './pages/Watch'; // members-only video page (2026-11-03)
import AffiliateIntake     from './pages/auth/AffiliateIntake';
import PartnerPortal       from './pages/partner/PartnerPortal';
import AdminPartnerDetail from './pages/admin/AdminPartnerDetail';

// Onboarding — Phase 1d ✅
import Onboarding from './pages/onboarding/Onboarding';

// Dashboard — Phase 3a ✅
import Dashboard    from './pages/dashboard/Dashboard';
import Integrations from './pages/dashboard/Integrations';
import Settings     from './pages/dashboard/Settings';
import CheckIn      from './pages/dashboard/CheckIn';
import Planning     from './pages/dashboard/Planning';
import Continuums   from './pages/dashboard/Continuums';
import LearningLab  from './pages/dashboard/LearningLab';
import Lesson       from './pages/dashboard/Lesson';
import Discussion     from './pages/dashboard/Discussion';
import DiscussionPost from './pages/dashboard/DiscussionPost';
import AnnouncementDetail from './pages/dashboard/AnnouncementDetail';
import AnnouncementArchive from './pages/dashboard/AnnouncementArchive';
import EventsAndReplays   from './pages/dashboard/EventsAndReplays';
import JoinLiveEvent     from './pages/dashboard/JoinLiveEvent';
import Replays            from './pages/dashboard/Replays';
import QuarterlyCheckin   from './pages/dashboard/QuarterlyCheckin';

// Admin — Phase 3b ✅
import Admin            from './pages/admin/Admin';
import AdminOverview    from './pages/admin/AdminOverview';
import MemberDetail      from './pages/admin/MemberDetail';
import AdminCourses      from './pages/admin/AdminCourses';
import AdminCourseDetail from './pages/admin/AdminCourseDetail';
import AdminCommunity    from './pages/admin/AdminCommunity';
import AdminHubEvents    from './pages/admin/AdminHubEvents';
import AdminBulkEmail    from './pages/admin/AdminBulkEmail';
import AdminBackend      from './pages/admin/AdminBackend';
import AdminTodos        from './pages/admin/AdminTodos';
import AdminMemberMigration from './pages/admin/AdminMemberMigration';
import AdminSequenceContent from './pages/admin/AdminSequenceContent';
import AdminBunnyLibrary from './pages/admin/AdminBunnyLibrary';
import AdminHiveLibrary from './pages/admin/AdminHiveLibrary';
import AdminRequests from './pages/admin/AdminRequests';
import TellUs from './pages/dashboard/TellUs';
import BookCall from './pages/dashboard/BookCall';
import AdminAffiliates   from './pages/admin/AdminAffiliates';
import AdminCoupons      from './pages/admin/AdminCoupons';
import AdminAffiliateDetail from './pages/admin/AdminAffiliateDetail';
import AdminAffiliatePreview from './pages/admin/AdminAffiliatePreview';
import AdminAffiliateControlRoom from './pages/admin/AdminAffiliateControlRoom';
import AdminSalesMarketing from './pages/admin/AdminSalesMarketing';
import AdminMoney from './pages/admin/AdminMoney';
import AffiliateDashboard from './pages/dashboard/AffiliateDashboard';

function UpdateBanner() {
  const updateAvailable = useVersionCheck();
  if (!updateAvailable) return null;
  return (
    <div
      style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999 }}
      className="bg-reset-terracotta text-white flex items-center justify-between px-4 py-3 shadow-lg"
    >
      <span className="text-sm font-medium">🔄 A new version is available</span>
      <button
        onClick={() => window.location.reload(true)}
        className="text-sm font-bold underline ml-4"
      >
        Tap to update
      </button>
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
    <AuthProvider>
      <Router>
        <Routes>
          {/* ── Public auth routes ── */}
          <Route path="/auth/login"           element={<Login />} />
          <Route path="/auth/signup"          element={<Signup />} />
          <Route path="/auth/forgot-password" element={<ForgotPassword />} />
          <Route path="/auth/reset-password"  element={<ResetPassword />} />
          <Route path="/auth/callback"        element={<OAuthCallback />} />
          <Route path="/auth/magic"           element={<MagicLinkVerify />} />
          <Route path="/join"                 element={<Join />} />
          <Route path="/join/success"         element={<JoinSuccess />} />
          <Route path="/watch/:videoId"       element={<Watch />} />
          <Route path="/affiliate-intake/:partnerId/:token" element={<AffiliateIntake />} />
          <Route path="/partner"              element={<PartnerPortal />} />
          <Route path="/auth/set-password"    element={
            <ProtectedRoute>
              <SetPassword />
            </ProtectedRoute>
          } />

          {/* ── Onboarding ── */}
          <Route
            path="/onboarding"
            element={
              <ProtectedRoute>
                <Onboarding />
              </ProtectedRoute>
            }
          />

          {/* ── Dashboard ── */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/integrations"
            element={
              <ProtectedRoute>
                <Integrations />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/settings"
            element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/tell-us"
            element={
              <ProtectedRoute>
                <TellUs />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/book-a-call"
            element={
              <ProtectedRoute>
                <BookCall />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/planning"
            element={
              <ProtectedRoute>
                <Planning />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/quarterly-checkin"
            element={
              <ProtectedRoute>
                <QuarterlyCheckin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/continuums"
            element={
              <ProtectedRoute>
                <Continuums />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/learning-lab"
            element={
              <ProtectedRoute>
                <LearningLab />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/learning-lab/:courseId/:lessonId"
            element={
              <ProtectedRoute>
                <Lesson />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/discussion"
            element={
              <ProtectedRoute>
                <Discussion />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/discussion/:id"
            element={
              <ProtectedRoute>
                <DiscussionPost />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/announcements"
            element={
              <ProtectedRoute>
                <AnnouncementArchive />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/announcements/:id"
            element={
              <ProtectedRoute>
                <AnnouncementDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/live/:eventId"
            element={
              <ProtectedRoute>
                <JoinLiveEvent />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/events"
            element={
              <ProtectedRoute>
                <EventsAndReplays />
              </ProtectedRoute>
            }
          />
          <Route
            path="/dashboard/replays"
            element={
              <ProtectedRoute>
                <Replays />
              </ProtectedRoute>
            }
          />

          {/* ── Admin (Phase 3b) ── */}
          <Route
            path="/admin"
            element={<ProtectedRoute adminOnly><AdminOverview /></ProtectedRoute>}
          />
          <Route
            path="/admin/members"
            element={<ProtectedRoute adminOnly><Admin /></ProtectedRoute>}
          />
          <Route
            path="/admin/members/:id"
            element={<ProtectedRoute adminOnly><MemberDetail /></ProtectedRoute>}
          />
          <Route
            path="/admin/courses"
            element={<ProtectedRoute adminOnly><AdminCourses /></ProtectedRoute>}
          />
          <Route
            path="/admin/courses/:id"
            element={<ProtectedRoute adminOnly><AdminCourseDetail /></ProtectedRoute>}
          />
          <Route
            path="/admin/community"
            element={<ProtectedRoute adminOnly><AdminCommunity /></ProtectedRoute>}
          />
          <Route
            path="/admin/hub-events"
            element={<ProtectedRoute adminOnly><AdminHubEvents /></ProtectedRoute>}
          />
          <Route
            path="/admin/bulk-email"
            element={<ProtectedRoute adminOnly><AdminBulkEmail /></ProtectedRoute>}
          />
          <Route
            path="/admin/backend"
            element={<ProtectedRoute adminOnly><AdminBackend /></ProtectedRoute>}
          />
          <Route
            path="/admin/todos"
            element={<ProtectedRoute adminOnly><AdminTodos /></ProtectedRoute>}
          />
          <Route
            path="/admin/member-migration"
            element={<ProtectedRoute adminOnly><AdminMemberMigration /></ProtectedRoute>}
          />
          <Route
            path="/admin/announcements"
            element={<Navigate to="/admin/sequence-content" replace />}
          />
          <Route
            path="/admin/sequence-content"
            element={<ProtectedRoute adminOnly><AdminSequenceContent /></ProtectedRoute>}
          />
          <Route
            path="/admin/bunny-library"
            element={<ProtectedRoute adminOnly><AdminBunnyLibrary /></ProtectedRoute>}
          />
          <Route
            path="/admin/hive-library"
            element={<ProtectedRoute adminOnly><AdminHiveLibrary /></ProtectedRoute>}
          />
          <Route
            path="/admin/requests"
            element={<ProtectedRoute adminOnly><AdminRequests /></ProtectedRoute>}
          />
          <Route
            path="/admin/affiliate-manager"
            element={<ProtectedRoute adminOnly affiliateManagerOnly><AdminAffiliateControlRoom /></ProtectedRoute>}
          />
          <Route
            path="/admin/sales-marketing"
            element={<ProtectedRoute adminOnly salesMarketingOnly><AdminSalesMarketing /></ProtectedRoute>}
          />
          <Route
            path="/admin/money"
            element={<ProtectedRoute adminOnly affiliateManagerOnly><AdminMoney /></ProtectedRoute>}
          />
          <Route
            path="/admin/affiliates"
            element={<ProtectedRoute adminOnly affiliateManagerOnly><AdminAffiliates /></ProtectedRoute>}
          />
          <Route
            path="/admin/affiliates/:id"
            element={<ProtectedRoute adminOnly affiliateManagerOnly><AdminAffiliateDetail /></ProtectedRoute>}
          />
          <Route
            path="/admin/partners/:id"
            element={<ProtectedRoute adminOnly affiliateManagerOnly><AdminPartnerDetail /></ProtectedRoute>}
          />
          <Route
            path="/admin/affiliates/:id/preview"
            element={<ProtectedRoute adminOnly affiliateManagerOnly><AdminAffiliatePreview /></ProtectedRoute>}
          />
          <Route
            path="/admin/coupons"
            element={<ProtectedRoute adminOnly><AdminCoupons /></ProtectedRoute>}
          />
          <Route
            path="/dashboard/affiliate"
            element={<ProtectedRoute><AffiliateDashboard /></ProtectedRoute>}
          />

          {/* ── Root redirect ── */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/auth/login" replace />} />
        </Routes>
        <UpdateBanner />
      </Router>
    </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
